import { api } from './authenticatedApi';

export interface FleetOverview {
  repoCount: number;
  analyzedCount: number;
  scoreDistribution: {
    min: number | null;
    max: number | null;
    mean: number | null;
    buckets: { label: string; count: number }[];
  };
  planTotals: {
    reposWithPlans: number;
    totalItems: number;
    automatableItems: number;
  };
  repositories: Array<{
    id: string;
    name: string;
    latestAnalysisScore: number | null;
    latestAnalysisAt: string | null;
    analysisStatus: string | null;
    isMonorepo?: boolean;
    projectCount?: number;
    planTotalItems: number | null;
    planAutomatable: number | null;
  }>;
}

export interface FleetInsightsDoc {
  org: string;
  fleet_size: number;
  exemplar_size: number;
  systemic_issues: Array<{
    title: string;
    entity_slug: string;
    kind: string;
    severity: string;
    message: string;
    affected_repos: string[];
    affected_count: number;
  }>;
  adoption: Record<
    string,
    { name: string; category: string; kind: string; count: number; pct: number }
  >;
  cooccurrence: Array<{ if: string; then: string; confidence: number; support: number }>;
  exemplar_deltas: Array<{
    slug: string;
    name: string;
    exemplar_adoption: string;
    fleet_adoption: string;
    exemplar_pct: number;
    fleet_pct: number;
  }>;
  best_of_fleet: {
    top_repos: string[];
    practices: Array<{ slug: string; name: string; top_pct: number; rest_pct: number }>;
  };
  modernizations?: Array<{
    from: string;
    to: string;
    task: string;
    llm_free: boolean;
    label: string;
    legacy_count: number;
    modern_count: number;
    legacy_name: string;
    candidate_repos: string[];
  }>;
  security_posture?: {
    reposWithSecurityGap: number;
    fleetSize: number;
    highSeverityFindings: number;
  };
}

export class FleetClientService {
  static async orgOverview(orgId: string) {
    return api.get<FleetOverview>(`/orgs/${orgId}/fleet/overview`);
  }

  static async orgInsights(orgId: string) {
    return api.get<{ insights: FleetInsightsDoc; generatedAt: string }>(
      `/orgs/${orgId}/fleet/insights`,
    );
  }

  static async reposUsingPractice(orgId: string, slug: string) {
    return api.get<PracticeReposResult>(
      `/orgs/${orgId}/practice-repos/${encodeURIComponent(slug)}`,
    );
  }
}

export interface PracticeRepoRow {
  id: string;
  name: string;
  fullName: string;
  latestAnalysisScore: number | null;
  latestAnalysisAt: string | null;
  analysisStatus: string | null;
  projectBreakdown?: { is_monorepo?: boolean; project_count?: number } | null;
  maintenancePlan: { totalItems: number; automatableCount: number } | null;
}

export interface PracticeReposResult {
  orgName: string;
  totalInOrg: number;
  repositories: PracticeRepoRow[];
}
